import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  Grid,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OmniPrepService from "../../../services/api/prepbusiness/prepbusiness"; // Update import path if needed

const SimulatorTab = () => {
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);

  // Meta fields
  const [spreadsheetId, setSpreadsheetId] = useState("SIMULATOR-TEST-ID");
  const [sheetName, setSheetName] = useState("SimulatorSheet");
  const [sheetVersion, setSheetVersion] = useState("v1");
  const [submit, setSubmit] = useState(false);

  // Paste input
  const [pasteInput, setPasteInput] = useState("");
  const [pasteError, setPasteError] = useState(null);

  const parsePastedRow = (text) => {
    const cols = text.trim().split("\t");
    if (cols.length < 4) return null;

    const location = cols[2]?.trim() || "Unknown";
    const qty = parseInt(cols[3]) || 1;

    // Find ASIN: B followed by exactly 9 uppercase letters/digits
    const asin =
      cols.find((c) => /^B[A-Z0-9]{9}$/.test(c.trim()))?.trim() || "";

    // Find total value: last column matching a German currency pattern (contains ,)
    let totalValue = 0;
    for (let i = cols.length - 1; i >= 0; i--) {
      const col = cols[i].trim().replace(/\s*€\s*$/, "");
      if (/^[\d.]+,[\d]{2}$/.test(col)) {
        totalValue = parseFloat(col.replace(/\./g, "").replace(",", ".")) || 0;
        break;
      }
    }

    return { location, qty, asin, totalValue };
  };

  const handlePasteRow = (e) => {
    const text = e.clipboardData?.getData("text") ?? e.target.value;
    if (!text.includes("\t")) return; // not a spreadsheet row
    e.preventDefault();
    const parsed = parsePastedRow(text);
    if (!parsed) {
      setPasteError(
        "Could not parse the pasted row — make sure it's tab-separated.",
      );
      return;
    }
    const newId = rows.length > 0 ? Math.max(...rows.map((r) => r.id)) + 1 : 1;
    setRows((prev) => [
      ...prev,
      {
        id: newId,
        asin: parsed.asin,
        qty: parsed.qty,
        totalValue: parsed.totalValue,
        location: parsed.location,
        sku: "",
        packages: "",
      },
    ]);
    setPasteInput("");
    setPasteError(null);
  };

  // Rows state
  const [rows, setRows] = useState([
    {
      id: 1,
      asin: "",
      qty: 1,
      totalValue: 0,
      location: "Unknown",
      sku: "",
      packages: "",
    },
  ]);

  const handleAddRow = () => {
    const newId = rows.length > 0 ? Math.max(...rows.map((r) => r.id)) + 1 : 1;
    setRows([
      ...rows,
      {
        id: newId,
        asin: "",
        qty: 1,
        totalValue: 0,
        location: "Unknown",
        sku: "",
        packages: "",
      },
    ]);
  };

  const handleDeleteRow = (id) => {
    setRows(rows.filter((r) => r.id !== id));
  };

  const handleRowChange = (id, field, value) => {
    setRows(rows.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  };

  const handleSimulate = async () => {
    setLoading(true);
    setError(null);
    setResponse(null);

    // Construct Payload
    // Backend expects:
    // class SheetRow(BaseModel):
    //    row_index: int
    //    data: Dict[str, Any]
    //
    // class GoogleSheetsBatchRequest(BaseModel):
    //    token: str
    //    spreadsheet_id: str
    //    sheet_id: int
    //    sheet_name: str
    //    version: str
    //    rows: List[SheetRow]

    const batchRows = rows.map((r, index) => ({
      row_index: index + 2, // 1-based, assuming header is row 1
      data: {
        ASIN: r.asin,
        Units: r.qty,
        "Total Value": r.totalValue,
        "EK-Ort": r.location,
        SKU: r.sku,
        ...(r.packages ? { Pakete: Number(r.packages) } : {}),
        // Add other potential keys if backend looks for them
        asin: r.asin,
        qty: r.qty,
        mfn: r.sku,
        sku: r.sku,
      },
    }));

    const rowPackageSplits = rows
      .map((r, index) => ({
        row_index: index + 2,
        packages: Number(r.packages),
      }))
      .filter((entry) => entry.packages >= 2);

    const payload = {
      token: "", // Backend might ignore or requires specific token
      spreadsheet_id: spreadsheetId,
      sheet_id: 0,
      sheet_name: sheetName,
      version: sheetVersion,
      submit: submit,
      rows: batchRows,
      ...(rowPackageSplits.length
        ? { row_package_splits: rowPackageSplits }
        : {}),
    };

    try {
      // We use the OmniPrepService.
      // Note: The method name in prepbusiness.js might be receiveBatch or sendBatch depending on exports.
      // Assuming 'sendBatch' based on user context or I'll check the file content if it breaks.
      // Previously I saw: sendBatch(payload) { return OmniApiClient.post(`/omni/prepbusiness/sheets/batch`, payload); }
      // But let's check what is actually exported.
      // Wait, I can't check easily without reading.
      // Based on my previous grep, it was sendBatch.

      // If sendBatch doesn't exist, I'll fallback to raw fetch or specific endpoint call.
      // But let's assume sendBatch exists.

      const res = await OmniPrepService.sendBatch(payload); // Actually check if method exists
      setResponse(res.data);
    } catch (err) {
      console.error(err);
      setError(
        // Handle 422 array of objects or simple string error
        Array.isArray(err.response?.data?.detail)
          ? JSON.stringify(err.response?.data?.detail, null, 2)
          : err.response?.data?.detail || err.message || "Simulation failed",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Bundle Simulation
      </Typography>
      <Typography variant="body2" color="textSecondary" mb={3}>
        Simulate a Google Sheets batch request to test bundling logic.
      </Typography>

      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Spreadsheet ID"
            value={spreadsheetId}
            onChange={(e) => setSpreadsheetId(e.target.value)}
            size="small"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Sheet Name"
            value={sheetName}
            onChange={(e) => setSheetName(e.target.value)}
            size="small"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Version"
            value={sheetVersion}
            onChange={(e) => setSheetVersion(e.target.value)}
            size="small"
          />
        </Grid>
      </Grid>

      <Card sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Paste a row from the spreadsheet
        </Typography>
        <Typography
          variant="caption"
          color="textSecondary"
          display="block"
          mb={1}
        >
          Paste a tab-separated row (date, title, <strong>EK-Ort</strong>,{" "}
          <strong>qty</strong>, …, <strong>ASIN</strong>, …,{" "}
          <strong>total value</strong>) and it will be added automatically.
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Paste a spreadsheet row here…"
          value={pasteInput}
          onChange={(e) => setPasteInput(e.target.value)}
          onPaste={handlePasteRow}
          inputProps={{ style: { fontFamily: "monospace", fontSize: 12 } }}
        />
        {pasteError && (
          <Alert
            severity="warning"
            sx={{ mt: 1 }}
            onClose={() => setPasteError(null)}
          >
            {pasteError}
          </Alert>
        )}
      </Card>

      <Card sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Input Data (Rows)
        </Typography>
        <TableContainer component={Paper} elevation={0} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ASIN</TableCell>
                <TableCell>SKU (Optional)</TableCell>
                <TableCell width={100}>Qty</TableCell>
                <TableCell
                  width={100}
                  title="Split row into N evenly sized packages"
                >
                  Packages
                </TableCell>
                <TableCell width={150}>Total Value (€)</TableCell>
                <TableCell>Location (EK-Ort)</TableCell>
                <TableCell width={50}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <TextField
                      fullWidth
                      variant="standard"
                      value={row.asin}
                      onChange={(e) =>
                        handleRowChange(row.id, "asin", e.target.value)
                      }
                      placeholder="B0..."
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      variant="standard"
                      value={row.sku}
                      onChange={(e) =>
                        handleRowChange(row.id, "sku", e.target.value)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      type="number"
                      variant="standard"
                      value={row.qty}
                      onChange={(e) =>
                        handleRowChange(
                          row.id,
                          "qty",
                          parseInt(e.target.value) || 0,
                        )
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      type="number"
                      variant="standard"
                      value={row.packages}
                      onChange={(e) =>
                        handleRowChange(row.id, "packages", e.target.value)
                      }
                      placeholder="e.g. 4"
                      inputProps={{ min: 2 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      type="number"
                      variant="standard"
                      value={row.totalValue}
                      onChange={(e) =>
                        handleRowChange(
                          row.id,
                          "totalValue",
                          parseFloat(e.target.value) || 0,
                        )
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      variant="standard"
                      value={row.location}
                      onChange={(e) =>
                        handleRowChange(row.id, "location", e.target.value)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteRow(row.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box mt={2}>
          <Button
            startIcon={<AddIcon />}
            onClick={handleAddRow}
            variant="outlined"
            size="small"
          >
            Add Row
          </Button>
        </Box>
      </Card>

      <Box
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        gap={2}
        mb={3}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={submit}
              onChange={(e) => setSubmit(e.target.checked)}
            />
          }
          label="Submit to PrepBusiness"
        />
        <Button
          variant="contained"
          onClick={handleSimulate}
          disabled={loading}
          sx={{ color: "white" }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Run Simulation"
          )}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {response && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Results
          </Typography>

          <Grid container spacing={2} mb={2}>
            <Grid item xs={4}>
              <Card sx={{ p: 2, textAlign: "center" }}>
                <Typography variant="subtitle2">Processed Rows</Typography>
                <Typography variant="h4">{response.processed_rows}</Typography>
              </Card>
            </Grid>
            <Grid item xs={4}>
              <Card sx={{ p: 2, textAlign: "center" }}>
                <Typography variant="subtitle2">Hazmat Items Found</Typography>
                <Typography variant="h4">
                  {response.hazmat_items_found}
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={4}>
              <Card sx={{ p: 2, textAlign: "center" }}>
                <Typography variant="subtitle2">Bundles Created</Typography>
                <Typography variant="h4">{response.bundles_created}</Typography>
              </Card>
            </Grid>
          </Grid>

          {response.bundles && response.bundles.length > 0 && (
            <Box>
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                Bundle Details
              </Typography>
              {response.bundles.map((bundle) => (
                <Accordion key={bundle.bundle_id}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      width="100%"
                      mr={2}
                    >
                      <Typography>Bundle #{bundle.bundle_id}</Typography>
                      <Typography color="textSecondary">
                        Value: {bundle.total_value?.toFixed(2)} € | Items:{" "}
                        {bundle.item_count} | Loc: {bundle.ek_ort}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>ASIN</TableCell>
                          <TableCell>SKU</TableCell>
                          <TableCell>Title</TableCell>
                          <TableCell align="right">Qty</TableCell>
                          <TableCell align="right">Value</TableCell>
                          <TableCell align="right">Dimensions (cm)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {bundle.items.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{item.asin}</TableCell>
                            <TableCell>{item.sku}</TableCell>
                            <TableCell>{item.title || "-"}</TableCell>
                            <TableCell align="right">{item.quantity}</TableCell>
                            <TableCell align="right">
                              {item.total_value?.toFixed(2)}
                            </TableCell>
                            <TableCell align="right">
                              {item.length}x{item.width}x{item.height}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}

          <Box mt={4}>
            <Typography variant="subtitle2">Raw Response Debug</Typography>
            <Paper
              variant="outlined"
              sx={(theme) => {
                const isDark = theme.palette.mode === "dark";
                return {
                  p: 2,
                  overflowX: "auto",
                  bgcolor: isDark
                    ? theme.palette.grey[50]
                    : theme.palette.grey[100],
                  borderColor: theme.palette.divider,
                  "& pre": {
                    m: 0,
                    fontFamily: "monospace",
                    fontSize: "0.8rem",
                    lineHeight: 1.5,
                    color: theme.palette.text.primary,
                    backgroundColor: "transparent",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  },
                };
              }}
            >
              <pre>{JSON.stringify(response, null, 2)}</pre>
            </Paper>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default SimulatorTab;
